import type { ReactNode } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { isInsideTelegram } from "@/lib/telegram";
import { DevLoginForm } from "@/app/DevLoginForm";
import { TelegramOnlyStub } from "@/app/TelegramOnlyStub";

interface Props {
  children: ReactNode;
  requireAdmin?: boolean;
  /** Admin OR trainer — used for /admin, where trainers can access
   *  training management even though they're not full admins. */
  requireStaff?: boolean;
}

export function ProtectedRoute({
  children,
  requireAdmin = false,
  requireStaff = false,
}: Props) {
  const { user, status, error } = useAuth();

  if (status === "loading" || status === "idle") {
    return <FullScreenLoader label="Авторизация…" />;
  }

  if (status !== "authenticated" || !user) {
    if (!isInsideTelegram()) {
      // Prod has no dev-login endpoint — show a proper landing. Dev keeps
      // the password form so local browser testing still works.
      return import.meta.env.PROD ? <TelegramOnlyStub /> : <DevLoginForm />;
    }
    return (
      <FullScreenMessage
        title="Нужно войти"
        subtitle={error ?? "Открой приложение через Telegram."}
      />
    );
  }

  if (requireAdmin && !user.is_admin) {
    return (
      <FullScreenMessage
        title="Нет доступа"
        subtitle="Этот раздел только для админов."
      />
    );
  }

  if (requireStaff && !user.is_admin && !user.is_trainer) {
    return (
      <FullScreenMessage
        title="Нет доступа"
        subtitle="Раздел доступен админам и тренерам."
      />
    );
  }

  return <>{children}</>;
}

function FullScreenLoader({ label }: { label: string }) {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "100dvh",
        gap: 16,
        color: "var(--fg2)",
      }}
    >
      <div
        style={{
          width: 28,
          height: 28,
          borderRadius: "50%",
          border: "2px solid var(--separator)",
          borderTopColor: "var(--brand-orange)",
          animation: "sp-spin 0.8s linear infinite",
        }}
      />
      <span className="caption">{label}</span>
    </div>
  );
}

function FullScreenMessage({ title, subtitle }: { title: string; subtitle: string }) {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "100dvh",
        padding: 24,
        textAlign: "center",
        gap: 8,
      }}
    >
      <h2 className="h2">{title}</h2>
      <p className="body" style={{ color: "var(--fg2)" }}>
        {subtitle}
      </p>
    </div>
  );
}
